import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { browserTimeZone } from '../timeZones.ts';
import type { IdentityProof } from './SecurityApi.ts';
import type { HttpClient } from '../http/HttpClient.ts';

export interface User {
  id: string;
  email: string;
  name: string;
  isPlatformAdmin: boolean;
}

export interface OrgMembership {
  id: string;
  name: string;
  slug: string;
  kind: 'personal' | 'team';
  /** Members must have two-factor on to open it. */
  requireTwoFactor: boolean;
  role: 'owner' | 'admin' | 'member' | 'guest';
  /** Paused over its owner's plan: listed, but it doesn't open. */
  paused: boolean;
  pause: { since: string; ownerName: string } | null;
}

/** Over your plan's people: how many, how many it allows, and by when to choose. */
export interface PlanNotice {
  people: number;
  allowed: number;
  graceUntil: string | null;
}

export interface Me {
  user: User;
  /** Whether two-factor (the authenticator app) is on. */
  twoFactor: boolean;
  planNotice: PlanNotice | null;
  orgs: OrgMembership[];
}

export interface SessionInfo {
  id: string;
  current: boolean;
  ip: string | null;
  userAgent: string | null;
  /** A readable name for the browser ("Chrome on macOS"). */
  device: string;
  createdAt: string;
  lastSeenAt: string;
}

/** Account endpoints (/auth/*). */
/** Signing in: done, or (two-factor on) the second step's challenge. */
export type SignInResult = { user: User } | { secondFactor: { challenge: string } };

export interface TwoFactorStatus {
  totp: boolean;
  recoveryCodesLeft: number;
}

export class AuthApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  me(): Promise<Me> {
    return this.#http.get<Me>('/auth/me');
  }

  /** Sends the browser's time zone too (for due-soon reminders and dates). */
  signup(input: { email: string; name: string; password: string }): Promise<void> {
    const timeZone = browserTimeZone();
    return this.#http.post('/auth/signup', timeZone ? { ...input, timeZone } : input);
  }

  resendVerification(email: string): Promise<void> {
    return this.#http.post('/auth/resend-verification', { email });
  }

  verifyEmail(token: string): Promise<{ user: User }> {
    return this.#http.post('/auth/verify-email', { token });
  }

  /** Signed in, or (two-factor on) the second step's challenge. */
  login(input: { email: string; password: string }): Promise<SignInResult> {
    return this.#http.post('/auth/login', input);
  }

  /** The second step: a code from the authenticator app, or a recovery code. */
  completeSecondFactor(
    input: { challenge: string } & ({ code: string } | { recoveryCode: string }),
  ): Promise<{ user: User }> {
    return this.#http.post('/auth/login/second-factor', input);
  }

  twoFactor(): Promise<{ twoFactor: TwoFactorStatus }> {
    return this.#http.get('/me/two-factor');
  }

  beginTwoFactor(): Promise<{ setup: { secret: string; otpauthUrl: string } }> {
    return this.#http.post('/me/two-factor/setup');
  }

  confirmTwoFactor(password: string, code: string): Promise<{ recoveryCodes: string[] }> {
    return this.#http.post('/me/two-factor/confirm', { password, code });
  }

  /** The password and a code (the server refuses without one). */
  disableTwoFactor(proof: IdentityProof): Promise<void> {
    return this.#http.post('/me/two-factor/disable', proof);
  }

  renewRecoveryCodes(proof: IdentityProof): Promise<{ recoveryCodes: string[] }> {
    return this.#http.post('/me/two-factor/recovery-codes', proof);
  }

  forgotPassword(email: string): Promise<void> {
    return this.#http.post('/auth/forgot-password', { email });
  }

  resetPassword(input: { token: string; password: string }): Promise<SignInResult> {
    return this.#http.post('/auth/reset-password', input);
  }

  /** `pushDevice`: this browser's push device (endpoint hash), forgotten with the session. */
  logout(pushDevice?: string | null): Promise<void> {
    return this.#http.post('/auth/logout', pushDevice ? { pushDevice } : {});
  }

  logoutEverywhere(): Promise<void> {
    return this.#http.post('/auth/logout-all');
  }

  sessions(): Promise<{ sessions: SessionInfo[] }> {
    return this.#http.get('/auth/sessions');
  }

  endSession(id: string): Promise<void> {
    return this.#http.delete(`/auth/sessions/${encodeURIComponent(id)}`);
  }

  /** A passkey sign-in's challenge, for the browser's prompt. */
  passkeySignInOptions(): Promise<{
    options: PublicKeyCredentialRequestOptionsJSON;
    ticket: string;
  }> {
    return this.#http.post('/auth/passkey/options');
  }

  signInWithPasskey(input: {
    ticket: string;
    response: AuthenticationResponseJSON;
  }): Promise<{ user: User }> {
    return this.#http.post('/auth/passkey', input);
  }
}
